import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import InfographicTab from './InfographicTab'
import type { InfographicConcept } from '../../types'

interface InfographicWorkspaceProps {
  infographic?: InfographicConcept
  onClose: () => void
}

export default function InfographicWorkspace({ infographic, onClose }: InfographicWorkspaceProps) {
  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] overflow-y-auto"
      onClick={(e) => {
        // Close if clicking the background (not the content)
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      {/* Dimmed backdrop with Shellby */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10">
          <div className="text-center">
            <img
              src="/shellby-avatar.png"
              alt="Shellby"
              className="w-48 h-48 mx-auto mb-4 opacity-50"
            />
            <p className="text-6xl font-bold text-[var(--bbs-color-accent)]">
              Infographic Studio
            </p>
          </div>
        </div>
      </div>

      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="fixed top-4 right-4 z-10 p-3 rounded-full bg-[var(--bbs-glass-card)] backdrop-blur-xl border border-[var(--bbs-color-border)] hover:bg-[var(--bbs-color-accent)]/10 transition-colors shadow-xl"
        aria-label="Close infographic workspace"
        title="Close (ESC)"
      >
        <X className="w-5 h-5 text-[var(--bbs-text-primary)]" />
      </button>

      {/* Content container */}
      <div className="relative z-10 max-w-7xl mx-auto p-4 sm:p-8">
        <div className="mb-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img
              src="/shellby-avatar.png"
              alt="Shellby"
              className="w-10 h-10 rounded-full"
            />
            <h1 className="text-3xl font-bold text-[var(--bbs-text-primary)]">
              Shellby's Infographic Studio
            </h1>
          </div>
          <p className="text-sm text-[var(--bbs-text-secondary)]">
            Generate, compare, and perfect your support infographics ✨
          </p>
        </div>

        {/* Infographic Tab Content */}
        <div className="card">
          <InfographicTab infographic={infographic} />
        </div>
      </div>
    </div>,
    document.body
  )
}
